import { format } from 'date-fns'
import { Check, Circle, RotateCw, X } from 'lucide-react'

import {
  JourneyStepStatusEnum,
  JourneyTimelineStep,
} from '@/app/services/journeyService'
import { useJourneyDetails } from '@/app/hooks/useJourneys'
import { useProductsCatalog } from '@/app/hooks/useProductsCatalog'
import masks from '@/app/utils/masks'
import { cn } from '@/app/utils/cn'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/view/components/ui/dialog'
import { Spinner } from '@/view/components/ui/spinner'

const duration = (ms: number | null) => {
  if (!ms) return null
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`

  return `${Math.round(ms / 60_000)}min`
}

const when = (value: string | null) =>
  value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : null

const StepIcon = ({ status }: { status: JourneyStepStatusEnum }) => {
  if (status === JourneyStepStatusEnum.SUCCESS)
    return <Check className="h-4 w-4 text-green-600" />
  if (status === JourneyStepStatusEnum.ERROR)
    return <X className="h-4 w-4 text-red-600" />
  if (status === JourneyStepStatusEnum.STARTED)
    return <RotateCw className="h-4 w-4 text-amber-500" />

  return <Circle className="h-4 w-4 text-gray-300" />
}

const StepCard = ({ step }: { step: JourneyTimelineStep }) => {
  const isPending = step.status === JourneyStepStatusEnum.PENDING
  return (
    <li className={cn('flex gap-3 py-3', isPending && 'opacity-50')}>
      <div className="mt-0.5">
        <StepIcon status={step.status} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="text-sm font-semibold">
            {step.order}. {step.label}
          </span>
          {step.attempts > 1 && (
            <span
              className="text-xs font-semibold text-amber-600"
              title="O vendedor refez esta etapa — deliberação, não erro"
            >
              {step.attempts} tentativas
            </span>
          )}
          {duration(step.durationMs) && (
            <span className="text-xs text-gray-400">
              {duration(step.durationMs)}
            </span>
          )}
        </div>

        {when(step.firstAttemptAt) && (
          <p className="text-xs text-gray-500">
            {when(step.firstAttemptAt)}
            {step.attempts > 1 && when(step.lastAttemptAt)
              ? ` → ${when(step.lastAttemptAt)}`
              : ''}
          </p>
        )}

        {step.error?.message && (
          <p className="mt-1 text-xs font-medium text-red-600">
            {step.error.message}
          </p>
        )}
      </div>
    </li>
  )
}

type JourneyTimelineModalProps = {
  journeyId?: string
  onClose: () => void
}

export const JourneyTimelineModal = ({
  journeyId,
  onClose,
}: JourneyTimelineModalProps) => {
  const { data, isLoading } = useJourneyDetails(journeyId)
  const { getProductLabel } = useProductsCatalog()

  return (
    <Dialog
      open={Boolean(journeyId)}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Jornada da simulação</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}

        {data && (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1 border-b border-gray01 pb-3 text-sm">
              <span>
                <span className="text-gray-400">Produto:</span>{' '}
                {getProductLabel(data.product)}
              </span>
              <span>
                <span className="text-gray-400">Vendedor:</span>{' '}
                {data.seller?.name ?? '—'}
              </span>
              <span>
                <span className="text-gray-400">Cliente:</span>{' '}
                {data.customer?.name ?? (data.cpf ? masks.cpf(data.cpf) : '—')}
              </span>
              <span>
                <span className="text-gray-400">Início:</span>{' '}
                {when(data.startedAt)}
              </span>
              {data.variant && (
                <span>
                  <span className="text-gray-400">Caminho:</span> {data.variant}
                </span>
              )}
            </div>

            <ul className="divide-y divide-gray01">
              {data.timeline.map((step) => (
                <StepCard key={step.key} step={step} />
              ))}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
